import type { VerticalDatumData } from "building-types/columns/columnDatumTypes";
import type { ParametricPosition } from "building-types/columns/porchColumnPattern";
import type { MaterialSrc } from "common/materials/materialSrc";
import type { DocEntityHandle } from "../handles/docEntityHandleTypes";
import type { ForeignEntityId } from "../handles/foreignEntityId";
import type { FoundationSectionSizeType } from "./foundationEnums";

export interface IsolatedFoundationPoint {
  x: number;
  y: number;
  foundationLevelId: ForeignEntityId<"LEVEL">;
  foundationAssemblyId: ForeignEntityId<"FOUNDATION_ASSEMBLY">;
  edgeAngle: number;
  topDatumId?: ForeignEntityId<"DATUM">;
  bottomDatumId?: ForeignEntityId<"DATUM">;
  sourcePickHandle?: DocEntityHandle;
}

export interface PierPosition extends ParametricPosition {
  readonly rotated?: boolean;
}
interface FoundationPatternBase<T extends FoundationSectionSizeType> {
  readonly patternType: T;
}

export type FoundationPattern = LinearFoundationPattern | VoidFoundationPattern | IsolatedFoundationPattern;

export type LinearFoundationPattern = FoundationPatternBase<FoundationSectionSizeType.LINEAR>;

export type IsolatedFoundationPattern = RoomIsolatedFoundationPattern | StairIsolatedFoundationPattern;

/** Runtime-only isolated foundation pattern generated from another selectable building entity. */
export type GeneratedIsolatedFoundationPattern = IsolatedFoundationPattern & {
  readonly sourcePickHandle?: DocEntityHandle;
};

interface IsolatedFoundationPatternParams {
  readonly positions: PierPosition[];
  readonly assemblyId: ForeignEntityId<"FOUNDATION_ASSEMBLY">;
  readonly xOffset: number;
  readonly yOffset: number;
  readonly pierRotation: number;
  readonly foundationLevelId?: ForeignEntityId<"LEVEL">;
  readonly topDatumId?: ForeignEntityId<"DATUM">;
  readonly bottomDatumId?: ForeignEntityId<"DATUM">;
  /** ID of the EdgeBehavior entity this pattern came from, if any. Used for 3D selection routing. */
  readonly edgeBehaviorId?: ForeignEntityId<"EDGE_BEHAVIOR">;
}
export interface RoomIsolatedFoundationPattern
  extends FoundationPatternBase<FoundationSectionSizeType.ISOLATED>,
    IsolatedFoundationPatternParams {
  readonly hostType: "ROOM";
}

// TODO: see https://github.com/higharc/product/pull/8725#discussion_r1203331473
export interface StairIsolatedFoundationPattern
  extends FoundationPatternBase<FoundationSectionSizeType.ISOLATED>,
    IsolatedFoundationPatternParams {
  readonly hostType: "STAIR_BLOCK";
  // posts
  readonly postId: ForeignEntityId<"COLUMN_DESCRIPTOR">; // here
  readonly postMaterial?: MaterialSrc;
  readonly verticalDatum: VerticalDatumData;
}

export type VoidFoundationPattern = FoundationPatternBase<FoundationSectionSizeType.VOID>;

export enum NearestMeasurementMethod {
  MID_PT = "MID_PT",
  PERP_DIST = "PERP_DIST",
}
